import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import ChaptersService from '../services/chapters'
import BackupService from '../services/backup'
import {
  cancelDebouncedPersist,
  cancelScheduledUpload,
  chapterPersistenceOrchestrator,
  debouncedPersistLocalDraft,
  flushDebouncedPersist,
  getActiveEditorChapter,
  setActiveEditorChapter,
  subscribeChapterPersistence,
  resumePendingUploadForOpenChapter,
} from '@/services/chapterPersistenceOrchestrator'

import { Chapter } from '../types'
import { ChapterBackup, SavingState } from '@/types/editor'
import { isDrasticChapterContentDeletion } from '@/utils/chapterContentDeletion'
import { isBackupPendingUpload, resolveSyncPhase } from '@/utils/chapterSyncPhase'
import {
  clearChapterSaveAuthDenialReports,
} from '@/utils/chapterSaveSentryContext'
import type { ProjectTypeValue } from '@/constants/projectTypes'
import isTipTapDoc from '@/utils/isTipTapDoc'
import { getHttpErrorStatus } from '@/utils/httpErrorStatus'
import { isAmbiguousSaveFailure } from '@/utils/isAmbiguousSaveFailure'
import { isConnectivityError } from '@/utils/isConnectivityError'
import { mergeChapterServerMetadata } from '@/utils/mergeChapterServerMetadata'
import {
  applyServerChapterToEditor,
  shouldApplyServerContentOnGet,
} from '@/utils/applyServerChapterToEditor'
import {
  ackContentRevision,
  ackTitleRevision,
  bumpContentLocalRevision,
  bumpTitleLocalRevision,
  clearChapterRevisions,
  forceAckAllRevisions,
  hasUnconfirmedEditorChanges,
} from '@/utils/chapterEditorRevisions'
import { runWithEditorContentReplaceOverride } from '@/utils/chapterEditorContentReplace'
import {
  normalizeContentVersion,
  readServerContentVersion,
  readServerLastContentSaveId,
} from '@/utils/chapterContentVersion'
import {
  clearChapterNextVersionCounter,
  resetChapterNextVersionCounter,
} from '@/utils/chapterNextVersion'
import { CHAPTER_STALE_SESSION_INACTIVITY_MS } from '@/constants/chapterStaleSession'
import {
  saveDiscardedChapterLocalCopy,
  saveDiscardedChapterLocalCopyFromChapter,
} from '@/utils/saveDiscardedChapterLocalCopy'
import {
  isSessionEnding,
  onCancelPendingPersistence,
  onFlushPendingBackup,
  onSessionEndDiscard,
} from '@/utils/sessionLifecycle'
import { getActiveNovelEditorJson } from '@/composables/useActiveNovelEditor'

export type LoadChapterResult =
  | { status: 'ok' }
  | { status: 'not-found' }
  | { status: 'gone'; backup: ChapterBackup | null }
  | { status: 'error' }

function isChapterBackupPending(backup: ChapterBackup | null | undefined): boolean {
  if (!backup) return false
  return isBackupPendingUpload(backup)
}

const baseContentVersionByChapter = new Map<string, number>()

function chapterScopeKey(projectId: string, chapterId: string): string {
  return `${projectId}:${chapterId}`
}

export const useCurrentChapterStore = defineStore('currentChapter', () => {
  const currentChapter = ref<Chapter | null>(null)
  const isLoading = ref(false)
  const savingState = ref<SavingState>('updated')
  const localBackup = ref<ChapterBackup | null>(null)
  const hasConflict = ref(false)
  const conflictData = ref({
    localData: null as ChapterBackup | null,
    serverData: null as Chapter | null,
  })
  const loadingChapterId = ref<string | null>(null)
  const loadedChapterBaseline = ref<{ jsonContent?: object } | null>(null)
  const showMassDeletionModal = ref(false)
  // When a local backup only has HTML, load it into the editor and save TipTap JSON once converted.
  const pendingLegacyBackupSync = ref(false)
  const pendingMassDeletionSave = ref<{
    chapterData: {
      _id: string
      title?: string
      jsonContent?: object
    }
    projectType: string
  } | null>(null)
  let inFlightSaves = 0
  let unsubscribePersistence: (() => void) | null = null
  const activeEditorProjectType = ref<string | undefined>(undefined)
  const sessionBaselineContentHash = ref<string | null>(null)
  const lastEditorContentChangeAt = ref(Date.now())
  const staleSessionCheckConsumed = ref(false)

  const reconcileEditorSavingStateFromBackup = () => {
    if (isSessionEnding()) return
    const chapter = currentChapter.value
    if (!chapter || inFlightSaves > 0) return
    if (hasConflict.value || savingState.value === 'conflict') return

    const backup = getLocalBackup(chapter._id, chapter.projectId)
    localBackup.value = backup

    if (!backup || !isChapterBackupPending(backup)) {
      if (
        savingState.value === 'pending changes'
        || savingState.value === 'local-backup'
        || savingState.value === 'offline'
        || savingState.value === 'updating'
      ) {
        savingState.value = 'updated'
      }
      if (!backup) {
        localBackup.value = null
      }
      return
    }

    const phase = resolveSyncPhase(backup)
    if (phase === 'auth_blocked') {
      savingState.value = 'local-backup'
      return
    }
    if (phase === 'offline') {
      savingState.value = 'offline'
      return
    }
    if (phase === 'conflict') {
      savingState.value = 'local-backup'
      return
    }
    if (phase === 'failed' && typeof navigator !== 'undefined' && navigator.onLine) {
      savingState.value = 'local-backup'
      return
    }
    if (phase === 'uploading') {
      savingState.value = 'updating'
      return
    }
    if (phase === 'needs_editor' && activeEditorProjectType.value) {
      pendingMassDeletionSave.value = {
        chapterData: {
          _id: chapter._id,
          title: chapter.title,
          jsonContent: chapter.jsonContent,
        },
        projectType: activeEditorProjectType.value,
      }
      showMassDeletionModal.value = true
      savingState.value = 'pending changes'
      return
    }
    if (phase === 'dirty' && savingState.value === 'updated') {
      savingState.value = 'pending changes'
    }
  }

  const ensurePersistenceListener = () => {
    if (unsubscribePersistence) return
    unsubscribePersistence = subscribeChapterPersistence(() => {
      reconcileEditorSavingStateFromBackup()
    })
  }

  const queueLocalPersistAndUpload = (
    chapterId: string,
    title: string,
    jsonContent: object | undefined,
    projectId: string,
    legacyContent?: string,
    patchScope: 'title' | 'full' = 'full',
  ) => {
    const backup = getLocalBackup(chapterId, projectId)
    debouncedPersistLocalDraft({
      projectId,
      chapterId,
      title,
      jsonContent,
      content: legacyContent,
      userId: currentChapter.value?.userId,
      baseUpdatedAt: resolveBaseUpdatedAt(),
      baseContentVersion: resolveBaseContentVersion(chapterId, projectId),
      inDoubtSaveIds: backup?.inDoubtSaveIds,
      order: currentChapter.value?._id === chapterId ? currentChapter.value.order : undefined,
      patchScope,
    })
  }

  // Emergency path only: used when jsonContent is not a TipTap doc so the editor can hydrate from HTML.
  const chapterLegacyContent = computed(() => {
    if (isTipTapDoc(currentChapter.value?.jsonContent)) return ''
    return currentChapter.value?.content || ''
  })
  const chapterJsonContent = computed(() => currentChapter.value?.jsonContent || {})
  const chapterTitle = computed(() => currentChapter.value?.title || '')
  const chapterId = computed(() => currentChapter.value?._id || '')
  const hasLocalBackup = computed(() => isChapterBackupPending(localBackup.value))
  const isOffline = computed(() => savingState.value === 'offline')
  const hasPendingChanges = computed(() => savingState.value === 'pending changes')

  const hasUnsyncedLocalChanges = () => {
    const state = savingState.value
    return state === 'pending changes' || state === 'local-backup' || state === 'offline'
  }

  onCancelPendingPersistence(() => {
    const chapter = currentChapter.value
    if (chapter) {
      cancelDebouncedPersist(chapter.projectId, chapter._id)
    }
  })
  const showBackupChapter = (backup: ChapterBackup) => {
    currentChapter.value = {
      _id: backup.chapterId,
      title: backup.title,
      content: isTipTapDoc(backup.jsonContent) ? undefined : backup.content,
      jsonContent: backup.jsonContent || {},
      projectId: backup.projectId,
      userId: currentChapter.value?.userId || '',
      order: currentChapter.value?.order || 0,
      updatedAt: backup.baseUpdatedAt || backup.savedAt,
    }
    localBackup.value = backup
    if (backup.baseContentVersion !== undefined) {
      confirmBaseContentVersion(backup.chapterId, backup.projectId, backup.baseContentVersion)
    }
    setLoadedChapterBaseline(currentChapter.value)
    setActiveEditorChapter({ projectId: backup.projectId, chapterId: backup.chapterId })
  }

  const keepLocalAndGoOffline = (chapterId: string) => {
    if (currentChapter.value?._id !== chapterId) return

    forceSaveToLocalBackup()
    savingState.value = navigator.onLine ? 'pending changes' : 'offline'

    const backup = getLocalBackup(chapterId, currentChapter.value.projectId)
    const projectId = currentChapter.value.projectId
    const active = getActiveEditorChapter()
    const isActiveChapter =
      active?.projectId === projectId && active?.chapterId === chapterId

    if (backup && !isActiveChapter) {
      chapterPersistenceOrchestrator.scheduleUpload(backup.projectId, chapterId, {
        delayMs: 0,
        force: true,
      })
      return
    }

    if (backup && isActiveChapter && navigator.onLine) {
      chapterPersistenceOrchestrator.scheduleForegroundUploadRetry(
        projectId,
        chapterId,
      )
    }
  }

  const loadChapter = async (chapterId: string): Promise<LoadChapterResult> => {
    if (!chapterId) return { status: 'error' }

    ensurePersistenceListener()
    loadingChapterId.value = chapterId
    if (currentChapter.value) {
      flushDebouncedPersist(currentChapter.value.projectId, currentChapter.value._id)
    }

    try {
      isLoading.value = true

      await BackupService.whenReady()
      if (loadingChapterId.value !== chapterId) return { status: 'error' }

      const backup = getLocalBackup(chapterId)
      if (backup) {
        showBackupChapter(backup)
        if (!isChapterBackupPending(backup)) {
          localBackup.value = null
        }
        isLoading.value = false
      }

      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        if (!backup) {
          savingState.value = 'error'
          currentChapter.value = null
          return { status: 'error' }
        }
        if (!isChapterBackupPending(backup)) {
          savingState.value = 'offline'
          return { status: 'ok' }
        }
        keepLocalAndGoOffline(chapterId)
        return { status: 'ok' }
      }

      savingState.value = 'updating'

      const response = await ChaptersService.get(chapterId)
      const serverChapter = response.data

      if (loadingChapterId.value !== chapterId) return { status: 'error' }

      if (currentChapter.value) {
        flushDebouncedPersist(currentChapter.value.projectId, currentChapter.value._id)
      }

      const conflictDetected = checkForLocalConflicts(serverChapter)

      if (loadingChapterId.value !== chapterId) return { status: 'error' }

      if (conflictDetected) {
        savingState.value = 'conflict'
        hasConflict.value = true
      } else if (pendingLegacyBackupSync.value) {
        savingState.value = 'pending changes'
        hasConflict.value = false
      } else if (inFlightSaves > 0) {
        hasConflict.value = false
      } else if (localBackup.value) {
        hasConflict.value = false
        savingState.value = 'pending changes'
      } else {
        savingState.value = 'updated'
        hasConflict.value = false
      }

      if (!conflictDetected && currentChapter.value) {
        resetStaleSessionTracking(currentChapter.value.contentHash)
      }

      syncActiveEditorChapterFromMemory()

      if (
        !conflictDetected
        && !hasConflict.value
        && currentChapter.value
        && isChapterBackupPending(getLocalBackup(chapterId, currentChapter.value.projectId))
      ) {
        resumePendingUploadForOpenChapter(
          currentChapter.value.projectId,
          currentChapter.value._id,
        )
      }

      return { status: 'ok' }
    } catch (error) {
      console.error('Error loading chapter:', error)

      if (loadingChapterId.value !== chapterId) return { status: 'error' }

      const httpStatus = getHttpErrorStatus(error)
      const backupAfterError = getLocalBackup(chapterId)

      if (httpStatus === 404) {
        currentChapter.value = null
        savingState.value = 'error'
        return { status: 'not-found' }
      }

      if (httpStatus === 410) {
        currentChapter.value = null
        savingState.value = 'error'
        return { status: 'gone', backup: backupAfterError }
      }

      if (isConnectivityError(error) && backupAfterError) {
        if (currentChapter.value?._id !== chapterId) {
          showBackupChapter(backupAfterError)
        }
        if (!isChapterBackupPending(backupAfterError)) {
          localBackup.value = null
          savingState.value = 'offline'
          return { status: 'ok' }
        }
        keepLocalAndGoOffline(chapterId)
        return { status: 'ok' }
      }

      if (backupAfterError) {
        showBackupChapter(backupAfterError)
        savingState.value = 'local-backup'
        return { status: 'ok' }
      }

      savingState.value = 'error'
      currentChapter.value = null
      return { status: 'error' }
    } finally {
      window.setTimeout(() => {
        if (loadingChapterId.value === chapterId) {
          isLoading.value = false
          loadingChapterId.value = null
        }
      }, 300)
    }
  }

  const setLoadedChapterBaseline = (chapter: Chapter) => {
    loadedChapterBaseline.value = {
      jsonContent: chapter.jsonContent,
    }
  }

  const resolveBaseUpdatedAt = (): string | undefined => {
    return localBackup.value?.baseUpdatedAt || currentChapter.value?.updatedAt || undefined
  }

  const resolveBaseContentVersion = (chapterId: string, projectId?: string): number => {
    const resolvedProjectId = projectId || currentChapter.value?.projectId
    if (resolvedProjectId) {
      const scoped = chapterScopeKey(resolvedProjectId, chapterId)
      if (baseContentVersionByChapter.has(scoped)) {
        return baseContentVersionByChapter.get(scoped)!
      }
    }
    const backup = getLocalBackup(chapterId, resolvedProjectId)
    if (backup?.baseContentVersion !== undefined) {
      return normalizeContentVersion(backup.baseContentVersion)
    }
    if (currentChapter.value?._id === chapterId) {
      return readServerContentVersion(currentChapter.value)
    }
    return 0
  }

  const confirmBaseContentVersion = (
    chapterId: string,
    projectId: string,
    version: number,
  ): void => {
    const normalized = normalizeContentVersion(version)
    baseContentVersionByChapter.set(
      chapterScopeKey(projectId, chapterId),
      normalized,
    )
    resetChapterNextVersionCounter(projectId, chapterId, normalized)
  }

  const applyServerChapterInMemory = (
    serverChapter: Chapter,
    applyContentFields: boolean,
  ): void => {
    if (!currentChapter.value) {
      currentChapter.value = serverChapter
      if (applyContentFields) {
        confirmBaseContentVersion(
          serverChapter._id,
          serverChapter.projectId,
          readServerContentVersion(serverChapter),
        )
        forceAckAllRevisions(serverChapter.projectId, serverChapter._id)
        setLoadedChapterBaseline(currentChapter.value)
      }
      return
    }

    const projectId = currentChapter.value.projectId
    const chapterId = currentChapter.value._id
    const apply = () => {
      currentChapter.value = applyServerChapterToEditor(
        currentChapter.value!,
        serverChapter,
        {
          projectId,
          chapterId,
          applyContentFields,
        },
      )
      if (applyContentFields) {
        confirmBaseContentVersion(
          chapterId,
          projectId,
          readServerContentVersion(serverChapter),
        )
        forceAckAllRevisions(projectId, chapterId)
        setLoadedChapterBaseline(currentChapter.value!)
      }
    }

    if (applyContentFields) {
      runWithEditorContentReplaceOverride(apply)
    } else {
      apply()
    }
  }

  const syncActiveEditorChapterFromMemory = () => {
    const chapter = currentChapter.value
    if (!chapter) {
      setActiveEditorChapter(null)
      return
    }
    setActiveEditorChapter({ projectId: chapter.projectId, chapterId: chapter._id })
  }

  const buildPatchParams = (
    chapterData: {
      title?: string
      jsonContent?: object
    },
    options?: {
      force?: boolean
      projectType?: ProjectTypeValue | string
      /** Override base; after conflict resolve-local, use server's updatedAt from 409. */
      expectedUpdatedAt?: string
    },
  ) => {
    const params: {
      title?: string
      jsonContent?: object
      force?: boolean
      expectedUpdatedAt?: string
      timeZone?: string
    } = {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }

    if (chapterData.title !== undefined) params.title = chapterData.title
    if (chapterData.jsonContent !== undefined) params.jsonContent = chapterData.jsonContent
    if (options?.force) params.force = true
    const expectedUpdatedAt = options?.expectedUpdatedAt ?? resolveBaseUpdatedAt()
    if (expectedUpdatedAt) {
      params.expectedUpdatedAt = expectedUpdatedAt
    }

    return params
  }

  const buildPatchPayloadForDeletionCheck = (chapterData: { jsonContent?: object }) =>
    chapterData.jsonContent !== undefined ? { jsonContent: chapterData.jsonContent } : {}

  const enterVersionConflictState = (serverChapter: Chapter) => {
    forceSaveToLocalBackup()
    const localData =
      localBackup.value || getLocalBackup(serverChapter._id, serverChapter.projectId)

    if (!localData) {
      savingState.value = 'local-backup'
      return
    }

    conflictData.value = {
      localData,
      serverData: serverChapter,
    }
    hasConflict.value = true
    savingState.value = 'conflict'
  }

  const resetStaleSessionTracking = (contentHash: string | null | undefined) => {
    sessionBaselineContentHash.value = contentHash ?? null
    lastEditorContentChangeAt.value = Date.now()
    staleSessionCheckConsumed.value = false
  }

  const clearStaleSessionTracking = () => {
    sessionBaselineContentHash.value = null
    staleSessionCheckConsumed.value = false
  }

  const isStaleSessionInactivityExceeded = (): boolean =>
    Date.now() - lastEditorContentChangeAt.value > CHAPTER_STALE_SESSION_INACTIVITY_MS

  const maybeRunStaleSessionContentCheck = async (): Promise<void> => {
    if (staleSessionCheckConsumed.value || hasConflict.value) return
    if (typeof navigator !== 'undefined' && !navigator.onLine) return
    if (!isStaleSessionInactivityExceeded()) return

    const chapter = currentChapter.value
    if (!chapter) return

    staleSessionCheckConsumed.value = true

    try {
      const syncResponse = await ChaptersService.checkContentSync(
        chapter._id,
        sessionBaselineContentHash.value,
      )
      if (syncResponse.data?.clean) {
        return
      }

      const fullResponse = await ChaptersService.get(chapter._id)
      const serverChapter = fullResponse.data as Chapter
      forceSaveToLocalBackup()
      enterVersionConflictState(serverChapter)
    } catch (error) {
      console.error('Stale session content sync check failed:', error)
      staleSessionCheckConsumed.value = false
    }
  }

  const saveChapter = async (
    chapterData: {
      _id: string
      title?: string
      jsonContent?: object
    },
    options?: {
      force?: boolean
      projectType?: ProjectTypeValue | string
      skipDeletionCheck?: boolean
      expectedUpdatedAt?: string
    },
  ) => {
    if (isSessionEnding() || !currentChapter.value) return

    if (showMassDeletionModal.value && !options?.force) {
      return
    }

    const savingChapterId = chapterData._id
    if (options?.projectType) {
      activeEditorProjectType.value = String(options.projectType)
    }
    const projectType = options?.projectType || activeEditorProjectType.value

    // Keep Pinia aligned with the payload we are about to persist
    if (currentChapter.value._id === savingChapterId) {
      if (chapterData.jsonContent !== undefined) {
        currentChapter.value.jsonContent = chapterData.jsonContent
      }
      if (chapterData.title !== undefined) {
        currentChapter.value.title = chapterData.title
      }
    }

    if (
      !options?.skipDeletionCheck &&
      !options?.force &&
      projectType &&
      loadedChapterBaseline.value &&
      isDrasticChapterContentDeletion(
        projectType,
        loadedChapterBaseline.value,
        buildPatchPayloadForDeletionCheck(chapterData),
      )
    ) {
      pendingMassDeletionSave.value = { chapterData, projectType }
      showMassDeletionModal.value = true
      savingState.value = 'pending changes'
      cancelScheduledUpload(currentChapter.value.projectId, savingChapterId)
      return
    }

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      keepLocalAndGoOffline(savingChapterId)
      return
    }

    const params = buildPatchParams(chapterData, options)
    const snapshotJson = JSON.stringify(currentChapter.value.jsonContent ?? null)
    const snapshotTitle = currentChapter.value.title
    inFlightSaves += 1

    forceSaveToLocalBackup()
    cancelScheduledUpload(currentChapter.value.projectId, savingChapterId)

    try {
      savingState.value = 'updating'

      const backupForUpload = getLocalBackup(savingChapterId, currentChapter.value.projectId)
      if (!backupForUpload) {
        savingState.value = 'error'
        return
      }

      const titleOnly =
        chapterData.jsonContent === undefined
        && chapterData.title !== undefined
        && backupForUpload.patchScope !== 'full'
      const result = await chapterPersistenceOrchestrator.uploadChapter(backupForUpload, {
        force: options?.force,
        silentConnectivityError: true,
        expectedUpdatedAt: options?.expectedUpdatedAt,
        titleOnly,
        allowWhileActiveEditor: true,
      })

      if (currentChapter.value?._id !== savingChapterId) return

      if (result.status === 'editor_moved') {
        if (result.chapter?.updatedAt && currentChapter.value) {
          currentChapter.value = {
            ...currentChapter.value,
            updatedAt: result.chapter.updatedAt,
          }
        }
        const editorMoved =
          JSON.stringify(currentChapter.value.jsonContent ?? null) !== snapshotJson ||
          currentChapter.value.title !== snapshotTitle
        if (editorMoved) {
          keepLocalAndGoOffline(savingChapterId)
          if (navigator.onLine) {
            savingState.value = 'pending changes'
          }
        } else {
          const latestBackup = getLocalBackup(savingChapterId, currentChapter.value.projectId)
          localBackup.value = latestBackup
          if (latestBackup && isChapterBackupPending(latestBackup)) {
            chapterPersistenceOrchestrator.scheduleUpload(
              latestBackup.projectId,
              savingChapterId,
              { delayMs: 0, force: true },
            )
            savingState.value = 'pending changes'
          } else {
            localBackup.value = null
            savingState.value = 'updated'
          }
        }
        return
      }

      if (result.status === 'success') {
        const savedChapter = result.chapter
        const editorMoved =
          JSON.stringify(currentChapter.value.jsonContent ?? null) !== snapshotJson ||
          currentChapter.value.title !== snapshotTitle
        if (editorMoved) {
          keepLocalAndGoOffline(savingChapterId)
          if (navigator.onLine) {
            savingState.value = 'pending changes'
          }
          return
        }

        if (currentChapter.value) {
          const projectId = currentChapter.value.projectId
          currentChapter.value = mergeChapterServerMetadata(
            currentChapter.value,
            savedChapter,
          )
          confirmBaseContentVersion(
            savingChapterId,
            projectId,
            readServerContentVersion(savedChapter),
          )
          if (chapterData.jsonContent !== undefined) {
            ackContentRevision(projectId, savingChapterId)
          }
          if (chapterData.title !== undefined) {
            ackTitleRevision(projectId, savingChapterId)
          }
          setLoadedChapterBaseline(currentChapter.value)
          resetStaleSessionTracking(savedChapter.contentHash)
        }

        const latestBackup = getLocalBackup(savingChapterId, currentChapter.value?.projectId)
        localBackup.value = isChapterBackupPending(latestBackup) ? latestBackup : null
        savingState.value = 'updated'
        pendingMassDeletionSave.value = null
        showMassDeletionModal.value = false
        pendingLegacyBackupSync.value = false
        clearChapterSaveAuthDenialReports(savingChapterId)
        return
      }

      if (result.status === 'needs_editor') {
        if (projectType) {
          pendingMassDeletionSave.value = { chapterData, projectType }
          showMassDeletionModal.value = true
          savingState.value = 'pending changes'
        }
        return
      }

      if (result.status === 'conflict') {
        if (result.serverChapter) {
          enterVersionConflictState(result.serverChapter)
        } else {
          savingState.value = 'conflict'
          hasConflict.value = true
        }
        return
      }

      if (result.status === 'offline' || result.status === 'failed') {
        keepLocalAndGoOffline(savingChapterId)
        if (result.status === 'failed' && navigator.onLine) {
          if (isAmbiguousSaveFailure(result.error)) {
            savingState.value = 'pending changes'
            return
          }
          savingState.value = 'local-backup'
        }
        return
      }

      if (result.status === 'auth_error') {
        savingState.value = 'local-backup'
      }
    } finally {
      inFlightSaves = Math.max(0, inFlightSaves - 1)
    }
  }

  const confirmMassDeletionSave = async () => {
    const pending = pendingMassDeletionSave.value
    if (!pending) return

    const { chapterData, projectType } = pending
    pendingMassDeletionSave.value = null
    showMassDeletionModal.value = false

    await saveChapter(chapterData, {
      force: true,
      projectType,
    })
  }

  const cancelMassDeletionSave = async () => {
    showMassDeletionModal.value = false
    pendingMassDeletionSave.value = null

    const chapterId = currentChapter.value?._id
    if (!chapterId) return

    if (currentChapter.value) {
      cancelDebouncedPersist(currentChapter.value.projectId, chapterId)
    }

    const restoreFromBaseline = () => {
      if (!currentChapter.value || !loadedChapterBaseline.value) return false

      currentChapter.value = {
        ...currentChapter.value,
        jsonContent: loadedChapterBaseline.value.jsonContent,
      }
      clearLocalBackup(chapterId)
      savingState.value = 'updated'
      return true
    }

    if (!navigator.onLine) {
      if (!restoreFromBaseline()) {
        savingState.value = 'pending changes'
      }
      return
    }

    try {
      savingState.value = 'updating'
      const response = await ChaptersService.get(chapterId)
      const serverChapter = response.data as Chapter

      if (currentChapter.value?._id !== chapterId) return

      saveDiscardedChapterLocalCopyFromChapter(currentChapter.value)
      runWithEditorContentReplaceOverride(() => {
        applyServerChapterInMemory(serverChapter, true)
      })
      BackupService.removeChapterBackup(serverChapter.projectId, chapterId)
      localBackup.value = null
      savingState.value = 'updated'
    } catch (error) {
      console.error('Error restoring chapter after mass deletion cancel:', error)

      if (currentChapter.value?._id !== chapterId) return

      if (!restoreFromBaseline()) {
        savingState.value = 'pending changes'
      }
    }
  }

  const updateChapterContent = (jsonContent: object, htmlContent?: string) => {
    if (!currentChapter.value) return

    void (async () => {
      await maybeRunStaleSessionContentCheck()
      if (!currentChapter.value) return

      lastEditorContentChangeAt.value = Date.now()

      const chapterId = currentChapter.value._id
      const title = currentChapter.value.title
      const projectId = currentChapter.value.projectId

      currentChapter.value.jsonContent = jsonContent
      // Keep HTML only for local backup / emergency legacy hydration — never for API patches.
      if (htmlContent !== undefined) {
        currentChapter.value.content = htmlContent
      }
      savingState.value = 'pending changes'

      bumpContentLocalRevision(projectId, chapterId)

      const legacyContent = currentChapter.value.content

      queueLocalPersistAndUpload(chapterId, title, jsonContent, projectId, legacyContent, 'full')

      if (pendingLegacyBackupSync.value && isTipTapDoc(jsonContent)) {
        void saveChapter({
          _id: chapterId,
          title,
          jsonContent,
        })
      }
    })()
  }

  const updateChapterTitle = (title: string) => {
    if (!currentChapter.value) return

    const chapterId = currentChapter.value._id
    const jsonContent = currentChapter.value.jsonContent
    const projectId = currentChapter.value.projectId
    const legacyContent = currentChapter.value.content

    currentChapter.value.title = title
    savingState.value = 'pending changes'

    bumpTitleLocalRevision(projectId, chapterId)

    queueLocalPersistAndUpload(chapterId, title, jsonContent, projectId, legacyContent, 'title')
  }

  const forceSaveToLocalBackup = () => {
    if (isSessionEnding() || !currentChapter.value) return

    const chapterId = currentChapter.value._id
    const title = currentChapter.value.title
    const liveJson = getActiveNovelEditorJson()
    const jsonContent = liveJson || currentChapter.value.jsonContent
    const projectId = currentChapter.value.projectId
    const legacyContent = currentChapter.value.content
    const userId = currentChapter.value.userId

    if (currentChapter.value) {
      cancelDebouncedPersist(projectId, chapterId)
    }
    saveToLocalBackup(chapterId, title, jsonContent, projectId, legacyContent, userId)
  }

  onFlushPendingBackup(() => {
    forceSaveToLocalBackup()
  })

  const discardInMemoryChapter = () => {
    const chapter = currentChapter.value
    if (chapter) {
      cancelDebouncedPersist(chapter.projectId, chapter._id)
    }
    loadingChapterId.value = null
    currentChapter.value = null
    setActiveEditorChapter(null)
    savingState.value = 'updated'
    localBackup.value = null
    hasConflict.value = false
    loadedChapterBaseline.value = null
    showMassDeletionModal.value = false
    pendingMassDeletionSave.value = null
    pendingLegacyBackupSync.value = false
    conflictData.value = {
      localData: null,
      serverData: null,
    }
    clearStaleSessionTracking()
  }

  onSessionEndDiscard(() => {
    discardInMemoryChapter()
  })

  /**
   * After an external sync stored the server chapter on the device, replace the
   * in-memory copy when it belongs to that project and is no longer pending.
   * Otherwise clearChapter would write the stale chapter back over the device copy.
   */
  const applySyncedDeviceChapter = (projectId: string) => {
    const chapter = currentChapter.value
    if (!chapter || chapter.projectId !== projectId) return

    const backup = getLocalBackup(chapter._id, projectId)
    if (!backup || isChapterBackupPending(backup)) return

    if (chapter) {
      cancelDebouncedPersist(projectId, chapter._id)
    }

    currentChapter.value = {
      ...chapter,
      title: backup.title,
      content: isTipTapDoc(backup.jsonContent) ? undefined : backup.content,
      jsonContent: backup.jsonContent || {},
      updatedAt: backup.baseUpdatedAt || chapter.updatedAt,
      order: typeof backup.order === 'number' ? backup.order : chapter.order,
    }
    setLoadedChapterBaseline(currentChapter.value)
    localBackup.value = null
    hasConflict.value = false
    conflictData.value = {
      localData: null,
      serverData: null,
    }
    pendingLegacyBackupSync.value = false
    savingState.value = 'updated'
  }

  const clearChapter = () => {
    const chapter = currentChapter.value
    if (chapter) {
      clearChapterNextVersionCounter(chapter.projectId, chapter._id)
      flushDebouncedPersist(chapter.projectId, chapter._id)
    }
    if (chapter && savingState.value !== 'updated' && savingState.value !== 'conflict') {
      forceSaveToLocalBackup()
      setActiveEditorChapter(null)
      chapterPersistenceOrchestrator.flushChapter(chapter.projectId, chapter._id)
    } else if (chapter) {
      cancelDebouncedPersist(chapter.projectId, chapter._id)
      setActiveEditorChapter(null)
    }
    loadingChapterId.value = null

    currentChapter.value = null
    setActiveEditorChapter(null)
    savingState.value = 'updated'
    localBackup.value = null
    hasConflict.value = false
    loadedChapterBaseline.value = null
    showMassDeletionModal.value = false
    pendingMassDeletionSave.value = null
    pendingLegacyBackupSync.value = false
    conflictData.value = {
      localData: null,
      serverData: null,
    }
  }

  const confirmDiscardUnsyncedChangesBeforeLogout = async (
    options: { force?: boolean; timeoutMs?: number } = {},
  ): Promise<boolean> => {
    const { force = false, timeoutMs = 5_000 } = options
    if (!currentChapter.value || !hasUnsyncedLocalChanges()) {
      return true
    }

    const liveJson = getActiveNovelEditorJson()
    const savePromise = saveChapter({
      _id: currentChapter.value._id,
      title: currentChapter.value.title,
      jsonContent: liveJson || currentChapter.value.jsonContent,
    })

    const timedOut = await Promise.race([
      savePromise.then(() => false),
      new Promise<boolean>((resolve) => {
        window.setTimeout(() => resolve(true), timeoutMs)
      }),
    ])

    if (!timedOut && !hasUnsyncedLocalChanges()) {
      return true
    }

    if (force) {
      return true
    }

    return window.confirm(
      'Tienes cambios sin sincronizar. Si cierras sesión se perderán. ¿Cerrar sesión igual?',
    )
  }

  const saveToLocalBackup = (
    chapterId: string,
    title: string,
    jsonContent: object | undefined,
    projectId: string,
    legacyContent?: string,
    userId?: string,
  ) => {
    if (isSessionEnding()) return

    let titleToSave = title
    let jsonContentToSave = jsonContent
    let legacyContentToSave = legacyContent
    let userIdToSave = userId || currentChapter.value?.userId

    if (currentChapter.value?._id === chapterId) {
      titleToSave = currentChapter.value.title
      jsonContentToSave = jsonContent || currentChapter.value.jsonContent
      legacyContentToSave = legacyContent || currentChapter.value.content
      userIdToSave = userId || currentChapter.value.userId
    }

    chapterPersistenceOrchestrator.persistLocalDraft({
      title: titleToSave || '',
      content: legacyContentToSave || undefined,
      jsonContent: jsonContentToSave || {},
      projectId,
      chapterId,
      userId: userIdToSave,
      baseUpdatedAt: resolveBaseUpdatedAt(),
      baseContentVersion: resolveBaseContentVersion(chapterId, projectId),
      inDoubtSaveIds: getLocalBackup(chapterId, projectId)?.inDoubtSaveIds,
      order: currentChapter.value?._id === chapterId ? currentChapter.value.order : undefined,
    })

    const storedBackup = BackupService.getChapterBackup(projectId, chapterId)
    if (currentChapter.value?._id === chapterId && storedBackup) {
      localBackup.value = storedBackup
    }
  }

  const getLocalBackup = (chapterId: string, projectId?: string): ChapterBackup | null => {
    const resolvedProjectId =
      projectId || currentChapter.value?.projectId || localBackup.value?.projectId

    if (!resolvedProjectId) {
      try {
        return BackupService.findChapterBackup(chapterId)
      } catch (error) {
        console.error('Error reading local backup:', error)
        return null
      }
    }

    try {
      return BackupService.getChapterBackup(resolvedProjectId, chapterId)
    } catch (error) {
      console.error('Error reading local backup:', error)
      return null
    }
  }

  const clearLocalBackup = (chapterId?: string) => {
    const chapter = currentChapter.value
    const idToUse = chapterId || chapter?._id
    const projectId = chapter?.projectId || localBackup.value?.projectId

    if (idToUse && projectId && BackupService.isProjectAvailableOffline(projectId)) {
      const source = chapter?._id === idToUse ? chapter : null
      if (source) {
        BackupService.saveSyncedChapterCopy({
          title: source.title,
          content: source.content,
          jsonContent: source.jsonContent || {},
          projectId,
          chapterId: idToUse,
          baseUpdatedAt: source.updatedAt,
          order: source.order,
        })
      }
      localBackup.value = null
      return
    }

    if (idToUse && projectId) {
      BackupService.removeChapterBackup(projectId, idToUse)
    }
    localBackup.value = null
  }

  const applyLocalBackupToChapter = (serverChapter: Chapter, backup: ChapterBackup) => {
    currentChapter.value = {
      ...serverChapter,
      title: backup.title,
      // Keep backup HTML only so EditableContent can hydrate when TipTap JSON is missing.
      content: isTipTapDoc(backup.jsonContent) ? undefined : backup.content,
      jsonContent: backup.jsonContent || {},
    }
  }

  const syncBackupThroughEditorOrSave = (
    chapterId: string,
    backup: ChapterBackup,
    options?: { expectedUpdatedAt?: string },
  ) => {
    if (isTipTapDoc(backup.jsonContent)) {
      pendingLegacyBackupSync.value = false
      void saveChapter(
        {
          _id: chapterId,
          title: backup.title,
          jsonContent: backup.jsonContent,
        },
        {
          expectedUpdatedAt: options?.expectedUpdatedAt,
          projectType: activeEditorProjectType.value,
        },
      )
      return
    }

    if (backup.content) {
      // Convert HTML via the editor, then persist only jsonContent.
      pendingLegacyBackupSync.value = true
      savingState.value = 'pending changes'
      return
    }

    pendingLegacyBackupSync.value = false
  }

  const checkForLocalConflicts = (serverChapter: Chapter): boolean => {
    setLoadedChapterBaseline(serverChapter)

    const backup = getLocalBackup(serverChapter._id, serverChapter.projectId)
    if (!backup) {
      currentChapter.value = serverChapter
      return false
    }

    if (!isChapterBackupPending(backup)) {
      currentChapter.value = serverChapter
      setLoadedChapterBaseline(serverChapter)
      localBackup.value = null
      if (BackupService.isProjectAvailableOffline(serverChapter.projectId)) {
        BackupService.saveSyncedChapterCopy({
          title: serverChapter.title,
          content: serverChapter.content,
          jsonContent: serverChapter.jsonContent || {},
          projectId: serverChapter.projectId,
          chapterId: serverChapter._id,
          baseUpdatedAt: serverChapter.updatedAt,
          order: serverChapter.order,
        })
      }
      return false
    }

    if (resolveSyncPhase(backup) === 'conflict') {
      applyLocalBackupToChapter(serverChapter, backup)
      localBackup.value = backup
      enterVersionConflictState(serverChapter)
      return true
    }

    const serverTime = new Date(serverChapter.updatedAt || 0).getTime()
    const localTime = backup.timestamp

    if (localTime > serverTime) {
      applyLocalBackupToChapter(serverChapter, backup)
      if (currentChapter.value && !backup.baseUpdatedAt) {
        currentChapter.value.updatedAt = serverChapter.updatedAt
      }
      syncBackupThroughEditorOrSave(serverChapter._id, backup, {
        expectedUpdatedAt: backup.baseUpdatedAt || serverChapter.updatedAt,
      })
      return false
    }

    const serverMatchesBackupBase =
      Boolean(backup.baseUpdatedAt) && backup.baseUpdatedAt === serverChapter.updatedAt

    if (serverMatchesBackupBase) {
      applyLocalBackupToChapter(serverChapter, backup)
      syncBackupThroughEditorOrSave(serverChapter._id, backup, {
        expectedUpdatedAt: backup.baseUpdatedAt,
      })
      return false
    }

    applyLocalBackupToChapter(serverChapter, backup)
    localBackup.value = backup
    enterVersionConflictState(serverChapter)
    return true
  }

  const resolveConflict = (choice: 'local' | 'server') => {
    if (!conflictData.value.localData || !conflictData.value.serverData) return

    hasConflict.value = false
    const localDataCopy = conflictData.value.localData
    const serverDataCopy = conflictData.value.serverData
    conflictData.value = {
      localData: null,
      serverData: null,
    }

    if (choice === 'local') {
      applyLocalBackupToChapter(serverDataCopy, localDataCopy)
      const serverVersion = readServerContentVersion(serverDataCopy)
      confirmBaseContentVersion(serverDataCopy._id, serverDataCopy.projectId, serverVersion)
      syncBackupThroughEditorOrSave(serverDataCopy._id, localDataCopy, {
        expectedUpdatedAt: serverDataCopy.updatedAt,
      })
    } else {
      pendingLegacyBackupSync.value = false
      saveDiscardedChapterLocalCopy(
        serverDataCopy.projectId,
        serverDataCopy._id,
        localDataCopy,
      )
      runWithEditorContentReplaceOverride(() => {
        applyServerChapterInMemory(serverDataCopy, true)
      })
      BackupService.removeChapterBackup(serverDataCopy.projectId, serverDataCopy._id)
      localBackup.value = null
      savingState.value = 'updated'
      resetStaleSessionTracking(serverDataCopy.contentHash)
    }
  }

  const setActiveEditorProjectTypeForChapter = (projectType: string | undefined) => {
    activeEditorProjectType.value = projectType ? String(projectType) : undefined
  }

  return {
    currentChapter,
    isLoading,
    savingState,
    localBackup,
    hasConflict,
    conflictData,
    showMassDeletionModal,
    pendingMassDeletionSave,
    loadedChapterBaseline,

    chapterLegacyContent,
    chapterJsonContent,
    chapterTitle,
    chapterId,
    hasLocalBackup,
    isOffline,
    hasPendingChanges,

    loadChapter,
    saveChapter,
    updateChapterContent,
    updateChapterTitle,
    clearChapter,
    discardInMemoryChapter,
    confirmDiscardUnsyncedChangesBeforeLogout,
    applySyncedDeviceChapter,
    resolveConflict,
    confirmMassDeletionSave,
    cancelMassDeletionSave,
    saveToLocalBackup,
    forceSaveToLocalBackup,
    getLocalBackup,
    clearLocalBackup,
    setActiveEditorProjectTypeForChapter,
  }
})
